import { useEffect, useMemo, useRef, useState } from "react";
import "../../CSS/ReportViewer.css";
import api from "../../api";

/* ════════════════════════════════════════════════════════════
   REPORT VIEWER — wired to Ilms_SaamiBackend2026's real endpoints:
     ConsBookingController:      get-booking-places, get-partylistAll, get-job-list/{partyId}
     ReportDefinitionController: get-report-fields, get-report-definitions, get-report-data
   ════════════════════════════════════════════════════════════ */
const COMPANY = {
  name: "SAAMI TRADESTAR LOGISTICS PVT LTD",
  gstin: "27AABCS1234A1ZP",
  addr: "Plot 14, Transport Nagar, Bhiwandi, Maharashtra 421302",
};

// Max options rendered in an autocomplete dropdown at once. With 15,000+
// parties, rendering every match makes the page freeze; the user narrows the
// list by typing, so only the first N matches are drawn.
const AC_MAX_SHOWN = 100;

// Presentational formatting hints for known numeric/date columns. The field
// list itself (id/name/cat/type) comes from GET /get-report-fields — this
// just decides *how* a "num" column is displayed (money vs plain vs weight).
const FMT_OVERRIDES = {
  invval: "money", freight: "money", other: "money", gst: "money", amount: "money", total: "money",
  chgwt: "wt", pcs: "int",
};

/* ─── FORMATTING HELPERS ─── */
function money(n) {
  return "₹" + (Math.round((n || 0) * 100) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
}
// Today as YYYY-MM-DD in the browser's LOCAL time (toISOString() is UTC and
// gives yesterday's date in India before 5:30 AM).
function todayStr() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}
function fmtVal(col, raw) {
  if (!col) return raw;
  if (raw === "" || raw === undefined || raw === null) return "—";
  if (col.type === "date") return fmtDate(raw);
  if (col.type === "num") {
    if (col.fmt === "money") return money(raw);
    if (col.fmt === "wt" || col.fmt === "dec") return (+raw).toFixed(1);
    return String(raw);
  }
  return raw;
}
// Grand / sub totals are calculated ONLY for amount-type columns — decided by
// the column name (Amount, Freight, Charges, GST, Total, Value, Fuel, Insurance…)
// or an existing money format. Every other column is left blank in the total row.
const AMOUNT_NAME_RE = /amount|amt|freight|charge|gst|tax|total|value|fuel|insurance|surcharge|rate/i;
function isAmountCol(c) {
  if (!c || c.type === "date") return false;
  if (c.fmt === "money") return true;
  return c.type === "num" && AMOUNT_NAME_RE.test(`${c.label || ""} ${c.id || ""}`);
}
function fmtTotal(col, raw) {
  if (col.fmt === "money" || isAmountCol(col)) return money(raw);
  if (col.fmt === "wt" || col.fmt === "dec") return (+raw).toFixed(1);
  return String(raw);
}
function totalsFor(cols, rows) {
  const t = {};
  cols.forEach((c) => {
    if (isAmountCol(c)) t[c.id] = rows.reduce((a, r) => a + (+r[c.id] || 0), 0);
  });
  return t;
}
const BADGE_CLASS = {
  DELIVERED: "repvi-badge-delivered",
  PARTIAL: "repvi-badge-transit",
  PENDING: "repvi-badge-pending",
  ATTEMPTED: "repvi-badge-attempted",
  RETURNED: "repvi-badge-attempted",
  OPEN: "repvi-badge-pending",
};
const STATUS_LABEL = (s) => {
  if (!s) return "—";
  const key = String(s).toUpperCase();
  return key.charAt(0) + key.slice(1).toLowerCase();
};

function StatusBadge({ status }) {
  const key = status ? String(status).toUpperCase() : "";
  return <span className={`repvi-badge ${BADGE_CLASS[key] || ""}`}>{STATUS_LABEL(status)}</span>;
}

/* ─── DOWNLOAD / FILE HELPERS ─── */
function fileName(report, ext) {
  return report.name.replace(/[^a-z0-9]+/gi, "_") + "_" + report.partyName.replace(/[^a-z0-9]+/gi, "_") + "_" + report.ff + "_to_" + report.ft + "." + ext;
}
function downloadBlob(name, content, mime) {
  const blob = new Blob(["\ufeff" + content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* Builds the raw HTML table markup shared by Excel export and Print/PDF. */
function reportTableHtml(report, forPrint) {
  const r = report;
  const head = "<tr>" + r.cols.map((c) => `<th class="${c.type === "num" ? "num" : ""}">${c.label}</th>`).join("") + "</tr>";
  const cell = (c, row) => {
    const v = row[c.id];
    if (c.id === "dlstatus") return `<td>${v}</td>`;
    const disp = fmtVal(c, v);
    return `<td class="${c.type === "num" ? "num" : ""}">${forPrint ? disp : (c.type === "num" && c.fmt === "money" ? String(v) : disp)}</td>`;
  };
  const sub = (rows, label, cls) => {
    const t = totalsFor(r.cols, rows);
    return `<tr class="${cls}">` + r.cols.map((c, i) => {
      if (i === 0) return `<td>${label}</td>`;
      if (isAmountCol(c)) return `<td class="num">${forPrint ? fmtTotal(c, t[c.id]) : t[c.id]}</td>`;
      return `<td class="${c.type === "num" ? "num" : ""}"></td>`;
    }).join("") + "</tr>";
  };
  let body = "";
  if (r.groupByBranch) {
    const byB = {};
    r.rows.forEach((x) => { const key = x._branch || "Unspecified"; (byB[key] = byB[key] || []).push(x); });
    Object.keys(byB).sort().forEach((br) => {
      body += `<tr class="grp"><td colspan="${r.cols.length}">${br} — ${byB[br].length} consignments</td></tr>`;
      body += byB[br].map((row) => "<tr>" + r.cols.map((c) => cell(c, row)).join("") + "</tr>").join("");
      body += sub(byB[br], br + " Sub-total", "subtotal");
    });
  } else {
    body = r.rows.map((row) => "<tr>" + r.cols.map((c) => cell(c, row)).join("") + "</tr>").join("");
  }
  return { head, body, grand: sub(r.rows, "Grand Total", "grand") };
}

/* ─── AUTOCOMPLETE COMPONENT ─── */
function AutoComplete({ options = [], value, onChange, placeholder = 'Search…', disabled = false }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(-1);
  const [cursorMoved, setCursorMoved] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  const selected = options.find((o) => String(o.value) === String(value));

  useEffect(() => {
    if (selected) setQuery(selected.label);
    else if (!value) setQuery('');
  }, [value, selected]);

  const filtered = options.filter(
    (o) => o.value !== undefined && o.label != null && String(o.label).toLowerCase().includes((query || '').toLowerCase())
  );
  // Only the first AC_MAX_SHOWN matches are rendered — keeps the page fast
  // with 15,000+ options. Keyboard navigation works on this same list.
  const shown = filtered.length > AC_MAX_SHOWN ? filtered.slice(0, AC_MAX_SHOWN) : filtered;

  useEffect(() => {
    if (focused >= 0 && optionRefs.current[focused]) {
      try { optionRefs.current[focused].scrollIntoView({ block: 'nearest' }); } catch (err) { /* noop */ }
    }
  }, [focused]);

  function hl(text, q) {
    if (!q) return text;
    const str = String(text);
    const i = str.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return str;
    return (
      <>
        {str.slice(0, i)}
        <mark>{str.slice(i, i + q.length)}</mark>
        {str.slice(i + q.length)}
      </>
    );
  }

  function choose(opt) {
    onChange(opt.value);
    setQuery(opt.label);
    setIsOpen(false);
    setFocused(-1);
    setCursorMoved(false);
  }

  function clear() {
    onChange('');
    setQuery('');
    setIsOpen(true);
    setFocused(-1);
  }

  useEffect(() => {
    function handle(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
        if (selected) setQuery(selected.label);
        else if (!value) setQuery('');
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [selected, value]);

  function handleKeyDown(e) {
    if (disabled) return;
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { setIsOpen(true); setCursorMoved(true); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault(); setCursorMoved(true);
      setFocused((f) => Math.min(f + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); setCursorMoved(true);
      setFocused((f) => Math.max(f - 1, 0));
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      if (selected) setQuery(selected.label);
      else if (!value) setQuery('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (cursorMoved && shown.length > 0) {
        const target = shown[focused] || shown[0];
        if (target) choose(target);
      } else if (filtered.length === 1) {
        choose(filtered[0]);
      } else {
        setIsOpen(false);
      }
    }
  }

  return (
    <div className="repvi-ac" ref={wrapRef}>
      <div className={'repvi-ac-box' + (isOpen ? ' repvi-ac-open' : '') + (disabled ? ' repvi-ac-disabled' : '')}>
        <input
          ref={inputRef}
          className="repvi-ac-in"
          type="text"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={query}
          onFocus={() => { if (!disabled) { setIsOpen(true); setCursorMoved(false); } }}
          onClick={() => { if (!disabled && !isOpen) setIsOpen(true); }}
          onChange={(e) => { if (disabled) return; setQuery(e.target.value); setIsOpen(true); setFocused(-1); }}
          onKeyDown={handleKeyDown}
        />
        {value && !disabled && (
          <span className="repvi-ac-x" onMouseDown={(e) => { e.preventDefault(); clear(); }}>×</span>
        )}
        <span className="repvi-ac-chev" onMouseDown={(e) => { if (disabled) return; e.preventDefault(); setIsOpen((o) => !o); }}>▾</span>
      </div>
      {isOpen && !disabled && (
        <div className="repvi-ac-drop">
          {filtered.length === 0 ? (
            <div className="repvi-ac-empty">No matches found</div>
          ) : (
            <>
              {shown.map((opt, i) => (
                <div
                  key={opt.value}
                  ref={(el) => { optionRefs.current[i] = el; }}
                  className={'repvi-ac-opt' + (String(opt.value) === String(value) ? ' repvi-ac-sel' : '') + (i === focused ? ' repvi-ac-foc' : '')}
                  onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
                  onMouseEnter={() => setFocused(i)}
                >
                  {hl(opt.label, query)}
                </div>
              ))}
              {filtered.length > AC_MAX_SHOWN && (
                <div className="repvi-ac-empty">Showing first {AC_MAX_SHOWN} of {filtered.length} — type to narrow</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
export default function ReportViewer() {
  const [fields, setFields] = useState([]);           // from GET /get-report-fields
  const [defs, setDefs] = useState([]);                 // from GET /get-report-definitions
  const [parties, setParties] = useState([]);           // from GET /get-partylistAll
  const [jobs, setJobs] = useState([]);                  // from GET /get-job-list/{partyId}, for the selected party
  const [bookingPlaces, setBookingPlaces] = useState([]); // from GET /get-booking-places

  const fieldById = (id) => fields.find((f) => f.id === id);

  const [branch, setBranch] = useState(""); // Place_Id of the selected booking place, or "" for All
  const [party, setParty] = useState("");   // Party_id
  const [job, setJob] = useState("");       // Job_Id
  const [from, setFrom] = useState(todayStr()); // defaults to current date
  const [to, setTo] = useState(todayStr());       // defaults to current date
  const [groupByBranchChk, setGroupByBranchChk] = useState(false);
  const [running, setRunning] = useState(false);

  const [report, setReport] = useState(null);

  const [emailOpen, setEmailOpen] = useState(false);
  const [attachFmt, setAttachFmt] = useState("excel");
  const [emTo, setEmTo] = useState("");
  const [emCc, setEmCc] = useState("");
  const [emSubject, setEmSubject] = useState("");
  const [emBody, setEmBody] = useState("");

  const [toasts, setToasts] = useState([]);
  const toastId = useRef(0);
  const printAreaRef = useRef(null);

  function toast(msg, type) {
    const id = ++toastId.current;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2900);
  }

  /* ── LOAD MASTER FIELD LIST, PARTIES & BOOKING PLACES ── */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/get-report-fields');
        setFields(res.data || []);
      } catch { toast("Could not load field list from server", "err"); }
    })();
    (async () => {
      try {
        const res = await api.get('/get-partylistAll');
        const normalized = (res.data || []).map((p) => ({
          Party_id: p.party_Id ?? p.Party_Id ?? p.Party_id ?? p.party_id,
          Party_Name: p.party_Name ?? p.Party_Name,
        }));
        setParties(normalized);
      } catch { toast("Could not load party list from server", "err"); }
    })();
    (async () => {
      try {
        const res = await api.get('/get-booking-places');
        // The API serializes Place_id as "place_id" (camelCase), so every
        // spelling is checked — otherwise Place_Id is undefined and the
        // dropdown drops every option.
        const normalized = (res.data || []).map((b) => ({
          Place_Id: b.place_Id ?? b.Place_Id ?? b.place_id ?? b.Place_id,
          Place_name: b.place_name ?? b.Place_name,
        }));
        setBookingPlaces(normalized);
      } catch { toast("Could not load booking places from server", "err"); }
    })();
    (async () => {
      try {
        const res = await api.get('/get-report-definitions');
        setDefs(res.data || []);
      } catch { toast("Could not load saved report definitions", "err"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── LOAD JOBS FOR THE SELECTED PARTY ── */
  useEffect(() => {
    if (!party) { setJobs([]); return; }
    (async () => {
      try {
        const res = await api.get(`/get-job-list/${party}`);
        const normalized = (res.data || []).map((j) => ({
          Job_Id: j.job_Id ?? j.Job_Id ?? j.job_id,
          Job_Name: j.job_Name ?? j.Job_Name,
        }));
        setJobs(normalized);
      } catch { toast("Could not load job list from server", "err"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [party]);

  /* ── DATE HANDLERS: no future dates, To can't be before From ── */
  function handleFromChange(v) {
    if (!v) { setFrom(""); return; }
    const today = todayStr();
    if (v > today) { toast("Future dates are not allowed", "warn"); v = today; }
    setFrom(v);
    if (to && to < v) setTo(v); // keep To >= From
  }
  function handleToChange(v) {
    if (!v) { setTo(""); return; }
    const today = todayStr();
    if (v > today) { toast("Future dates are not allowed", "warn"); v = today; }
    if (from && v < from) { toast("To Date cannot be before From Date", "warn"); v = from; }
    setTo(v);
  }

  function handlePartyChange(id) {
    setParty(id);
    setJob("");
    setReport(null);
  }

  /* ── RUN REPORT: pulls real rows for the saved definition's columns ── */
  async function runReport() {
    if (!party) { toast("Select a party", "warn"); return; }
    if (!job) { toast("Select a job", "warn"); return; }
    if (!from || !to) { toast("Select the period", "warn"); return; }
    if (from > todayStr() || to > todayStr()) { toast("Future dates are not allowed", "warn"); return; }
    if (to < from) { toast("To Date cannot be before From Date", "warn"); return; }

    const def = defs.find((d) => String(d.partyId) === String(party) && String(d.jobId) === String(job));
    if (!def) {
      toast('No saved report definition for this Party/Job — define one in Report Definition first', 'warn');
      setReport(null);
      return;
    }

    setRunning(true);
    try {
      const params = { partyId: party, jobId: job, from, to };
      if (branch) params.bookPlaceId = branch;
      const res = await api.get('/get-report-data', { params });
      const data = res.data;
      const cols = def.cols.map((c) => {
        const f = fieldById(c.id) || { type: 'text' };
        return { ...c, type: f.type, fmt: FMT_OVERRIDES[c.id] };
      });

      const p = parties.find((x) => String(x.Party_id) === String(party));
      const j = jobs.find((x) => String(x.Job_Id) === String(job));
      const bp = bookingPlaces.find((x) => String(x.Place_Id) === String(branch));

      setReport({
        party, job, branch: bp ? bp.Place_name : '', ff: from, ft: to,
        cols, rows: data.rows || [],
        groupByBranch: groupByBranchChk && !branch,
        name: def.name, hasDef: true,
        partyName: p ? p.Party_Name : `Party #${party}`,
        jobName: j ? j.Job_Name : `Job #${job}`,
      });
    } catch (e) {
      console.error('runReport error:', e);
      toast('Could not run report: ' + (e.response?.data?.message || e.message), 'err');
      setReport(null);
    } finally {
      setRunning(false);
    }
  }

  /* ── EXPORT: EXCEL ── */
  function exportExcelSilent(rep) {
    const t = reportTableHtml(rep, false);
    const meta = `Report: ${rep.name} | Party: ${rep.partyName} | Job: ${rep.jobName} | Branch: ${rep.branch || "All"} | Period: ${fmtDate(rep.ff)} to ${fmtDate(rep.ft)}`;
    const n = rep.cols.length;
    // Title + filter line live INSIDE the sheet's cells (merged across all
    // columns) instead of loose HTML above the table, so it reads as a real
    // worksheet. The x: block turns gridlines on and names the sheet.
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8">
      <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
        <x:Name>Report</x:Name>
        <x:WorksheetOptions><x:DisplayGridlines/><x:Print><x:ValidPrinterInfo/></x:Print></x:WorksheetOptions>
      </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
      <style>
        table{border-collapse:collapse;}
        th,td{border:.5pt solid #808080;padding:3px 6px;font-family:Calibri,Arial;font-size:11pt;mso-number-format:"\\@";vertical-align:middle;}
        th{background:#1a2a4a;color:#fff;font-weight:bold;text-align:center;}
        .num{mso-number-format:"0.00";text-align:right;}
        .ttl{font-size:14pt;font-weight:bold;border:none;background:#fff;}
        .meta{font-size:10pt;border:none;background:#fff;}
        tr.subtotal td{background:#e0e7ff;font-weight:bold;}
        tr.grand td{background:#1a2a4a;color:#fff;font-weight:bold;}
        tr.grp td{background:#334155;color:#fff;font-weight:bold;}
      </style></head>
      <body><table><thead>
        <tr><td class="ttl" colspan="${n}">${COMPANY.name}</td></tr>
        <tr><td class="meta" colspan="${n}">${meta}</td></tr>
        <tr><td class="meta" colspan="${n}"></td></tr>
        ${t.head}</thead><tbody>${t.body}</tbody><tfoot>${t.grand}</tfoot></table></body></html>`;
    downloadBlob(fileName(rep, "xls"), html, "application/vnd.ms-excel");
  }
  function exportExcel() {
    if (!report) return;
    exportExcelSilent(report);
    toast("Excel exported", "ok");
  }

  /* ── EXPORT: PDF (print) ── */
  function buildPrintArea() {
    const r = report, t = reportTableHtml(r, true);
    if (printAreaRef.current) {
      printAreaRef.current.innerHTML = `
        <div class="repvi-pa-hdr">
          <div><div class="repvi-pa-co">${COMPANY.name}</div><div class="repvi-pa-co-sub">${COMPANY.addr} · GSTIN ${COMPANY.gstin}</div></div>
          <div><div class="repvi-pa-rt">${r.name}</div><div class="repvi-pa-meta">Generated ${fmtDate(new Date().toISOString())}</div></div>
        </div>
        <div class="repvi-pa-filters"><b>Party:</b> ${r.partyName} &nbsp;|&nbsp; <b>Job:</b> ${r.jobName} &nbsp;|&nbsp; <b>Branch:</b> ${r.branch || "All Branches"} &nbsp;|&nbsp; <b>Period:</b> ${fmtDate(r.ff)} – ${fmtDate(r.ft)} &nbsp;|&nbsp; <b>Records:</b> ${r.rows.length}</div>
        <table class="repvi-pa-tbl"><thead>${t.head}</thead><tbody>${t.body}</tbody><tfoot>${t.grand}</tfoot></table>`;
    }
  }
  function exportPdf() {
    if (!report) return;
    buildPrintArea();
    toast('Opening print dialog — choose "Save as PDF"', "info");
    setTimeout(() => window.print(), 250);
  }

  /* ── EMAIL ── */
  function openEmail() {
    if (!report) return;
    const r = report;
    setEmSubject(`${r.name} — ${r.partyName} (${fmtDate(r.ff)} to ${fmtDate(r.ft)})`);
    setEmBody(`Dear Team,\n\nPlease find attached the ${r.name} for ${r.partyName} / ${r.jobName} (${r.branch || "All Branches"}) for the period ${fmtDate(r.ff)} to ${fmtDate(r.ft)}.\n\nTotal consignments: ${r.rows.length}\nTotal business: ${money(r.rows.reduce((a, x) => a + (+x.total || 0), 0))}\n\nRegards,\n${COMPANY.name}`);
    setEmTo("");
    setEmCc("");
    setAttachFmt("excel");
    setEmailOpen(true);
  }
  function sendEmail() {
    const to = emTo.trim();
    if (!to) { toast("Enter at least one recipient", "warn"); return; }
    if (attachFmt === "excel" || attachFmt === "both") exportExcelSilent(report);
    setEmailOpen(false);
    const list = to.split(",");
    toast("Report emailed to " + list[0] + (list.length > 1 ? " +" + (list.length - 1) : "") + " (" + attachFmt + ")", "ok");
  }
  function openMailClient() {
    const sub = encodeURIComponent(emSubject);
    const body = encodeURIComponent(emBody + "\n\n(Attach the downloaded report file before sending.)");
    window.location.href = `mailto:${emTo.trim()}?cc=${emCc.trim()}&subject=${sub}&body=${body}`;
  }

  /* ── DERIVED SUMMARY ── */
  const summary = report ? {
    cnr: report.rows.length,
    pcs: report.rows.reduce((a, x) => a + (+x.pcs || 0), 0).toLocaleString("en-IN"),
    wt: report.rows.reduce((a, x) => a + (+x.chgwt || 0), 0).toFixed(1),
    amt: money(report.rows.reduce((a, x) => a + (+x.total || 0), 0)).replace(".00", ""),
  } : null;

  const grandTotals = report ? totalsFor(report.cols, report.rows) : null;
  const groupedRows = useMemo(() => {
    if (!report || !report.groupByBranch) return null;
    const byBranch = {};
    report.rows.forEach((row) => { const key = row._branch || "Unspecified"; (byBranch[key] = byBranch[key] || []).push(row); });
    return Object.keys(byBranch).sort().map((br) => ({ branch: br, rows: byBranch[br] }));
  }, [report]);

  // Dropdown options are memoised so 15,000+ parties aren't re-mapped on
  // every keystroke / render.
  const branchOpts = useMemo(
    () => bookingPlaces.map((b) => ({ value: b.Place_Id, label: b.Place_name })),
    [bookingPlaces]
  );
  const partyOpts = useMemo(
    () => parties.map((p) => ({ value: p.Party_id, label: p.Party_Name })),
    [parties]
  );
  const jobOpts = useMemo(
    () => jobs.map((j) => ({ value: j.Job_Id, label: j.Job_Name })),
    [jobs]
  );

  return (
    <div className="repvi-page">
      {/* PAGE HEADER */}
      <div className="repvi-page-header">
        <div>
          <div className="repvi-page-title">Branch / Party / Job <span>Business Report</span></div>
          <div className="repvi-page-sub">Generate period-wise business details as per the saved report definition, then export or email.</div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="repvi-panel repvi-panel-filters">
        <div className="repvi-panel-head">
          <div className="repvi-panel-icon">🎯</div>
          <div>
            <div className="repvi-panel-title">Report Criteria</div>
            <div className="repvi-panel-sub">Branch · Party · Job · Period</div>
          </div>
        </div>
        <div className="repvi-panel-body">
          <div className="repvi-filters">
            <div className="repvi-field">
              <label>Branch</label>
              <AutoComplete
                options={branchOpts}
                value={branch}
                onChange={(v) => setBranch(v)}
                placeholder="All Branches"
              />
            </div>
            <div className="repvi-field">
              <label>Party <span className="repvi-req">*</span></label>
              <AutoComplete
                options={partyOpts}
                value={party}
                onChange={(v) => handlePartyChange(v)}
                placeholder="— Select Party —"
              />
            </div>
            <div className="repvi-field">
              <label>Job <span className="repvi-req">*</span></label>
              <AutoComplete
                options={jobOpts}
                value={job}
                onChange={(v) => setJob(v)}
                placeholder={!party ? "(select party first)" : "— Select Job —"}
                disabled={!party}
              />
            </div>
            <div className="repvi-field">
              <label>From Date <span className="repvi-req">*</span></label>
              <input type="date" value={from} max={todayStr()} onChange={(e) => handleFromChange(e.target.value)} />
            </div>
            <div className="repvi-field">
              <label>To Date <span className="repvi-req">*</span></label>
              <input type="date" value={to} min={from || undefined} max={todayStr()} onChange={(e) => handleToChange(e.target.value)} />
            </div>
            <div className="repvi-field">
              <label>&nbsp;</label>
              <button className="repvi-btn repvi-btn-primary" onClick={runReport} disabled={running}>{running ? '… Running' : '▶ Run Report'}</button>
            </div>
          </div>
          <div className="repvi-filters-row2">
            <label className="repvi-chkbox">
              <input type="checkbox" checked={groupByBranchChk} onChange={(e) => setGroupByBranchChk(e.target.checked)} />
              Group by Branch (with sub-totals)
            </label>
            <span className="repvi-hint">Columns &amp; totals come from the report definition for the selected Party + Job.</span>
          </div>
        </div>
      </div>

      {/* SUMMARY */}
      {summary && (
        <div className="repvi-summary-grid">
          <div className="repvi-summary-card" style={{ "--card-accent": "var(--accent2)" }}>
            <div className="repvi-summary-val">{summary.cnr}</div>
            <div className="repvi-summary-lbl">Consignments</div>
          </div>
          <div className="repvi-summary-card" style={{ "--card-accent": "var(--accent2)" }}>
            <div className="repvi-summary-val">{summary.pcs}</div>
            <div className="repvi-summary-lbl">Total Pcs</div>
          </div>
          <div className="repvi-summary-card" style={{ "--card-accent": "var(--success)" }}>
            <div className="repvi-summary-val">{summary.wt}</div>
            <div className="repvi-summary-lbl">Charged Weight (Kg)</div>
          </div>
          <div className="repvi-summary-card" style={{ "--card-accent": "var(--accent)" }}>
            <div className="repvi-summary-val">{summary.amt}</div>
            <div className="repvi-summary-lbl">Total Business (₹)</div>
          </div>
        </div>
      )}

      {/* REPORT OUTPUT */}
      <div className="repvi-panel">
        <div className="repvi-panel-head">
          <div className="repvi-panel-icon">📄</div>
          <div>
            <div className="repvi-panel-title">Report Output</div>
            <div className="repvi-panel-sub">{report ? `${report.name} · ${report.rows.length} records` : "Run a report to see results"}</div>
          </div>
          <div className="repvi-panel-right">
            <button className="repvi-btn repvi-btn-success repvi-btn-sm" disabled={!report || report.rows.length === 0} onClick={exportExcel}>⬇ Excel</button>
            <button className="repvi-btn repvi-btn-danger repvi-btn-sm" disabled={!report || report.rows.length === 0} onClick={exportPdf}>🖨 PDF</button>
            <button className="repvi-btn repvi-btn-accent2 repvi-btn-sm" disabled={!report || report.rows.length === 0} onClick={openEmail}>✉ Email</button>
          </div>
        </div>

        {report && (
          <div className="repvi-meta">
            <div className="repvi-meta-item">Report: <b>{report.name}</b></div>
            <div className="repvi-meta-item">Party: <b>{report.partyName}</b></div>
            <div className="repvi-meta-item">Job: <b>{report.jobName}</b></div>
            <div className="repvi-meta-item">Branch: <b>{report.branch || "All Branches"}</b></div>
            <div className="repvi-meta-item">Period: <b>{fmtDate(report.ff)} – {fmtDate(report.ft)}</b></div>
          </div>
        )}

        {report && !report.hasDef && (
          <div className="repvi-def-note">⚠ No saved report definition for this Party/Job — showing the standard column set. Define one in <b>&nbsp;Report Definition</b>.</div>
        )}

        {!report && (
          <div className="repvi-empty">
            <div className="repvi-empty-ico">📊</div>
            <div className="repvi-empty-t">No report yet</div>
            <div className="repvi-empty-s">Select Party, Job &amp; period, then click <b>Run Report</b></div>
          </div>
        )}

        {report && report.rows.length === 0 && (
          <div className="repvi-empty">
            <div className="repvi-empty-ico">🔍</div>
            <div className="repvi-empty-t">No records found</div>
            <div className="repvi-empty-s">No consignments match this party / job / branch / period</div>
          </div>
        )}

        {report && report.rows.length > 0 && (
          <div className="repvi-tbl-scroll">
            <table className="repvi-tbl">
              <thead>
                <tr>
                  {report.cols.map((c) => <th key={c.id} className={c.type === "num" ? "repvi-num" : ""}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {report.groupByBranch
                  ? groupedRows.flatMap((g) => {
                      const t = totalsFor(report.cols, g.rows);
                      return [
                        <tr key={`grp-${g.branch}`} className="repvi-grp">
                          <td colSpan={report.cols.length}>📍 {g.branch} — {g.rows.length} consignments</td>
                        </tr>,
                        ...g.rows.map((row, idx) => (
                          <tr key={row.cnr + idx}>
                            {report.cols.map((c) => (
                              <td key={c.id} className={c.type === "num" ? "repvi-num" : ""}>
                                {c.id === "dlstatus" ? <StatusBadge status={row[c.id]} /> : fmtVal(c, row[c.id])}
                              </td>
                            ))}
                          </tr>
                        )),
                        <tr key={`sub-${g.branch}`} className="repvi-subtotal">
                          {report.cols.map((c, i) => (
                            <td key={c.id} className={c.type === "num" ? "repvi-num" : ""}>
                              {i === 0 ? `${g.branch} Sub-total` : (isAmountCol(c) ? fmtTotal(c, t[c.id]) : "")}
                            </td>
                          ))}
                        </tr>,
                      ];
                    })
                  : report.rows.map((row, idx) => (
                      <tr key={row.cnr + idx}>
                        {report.cols.map((c) => (
                          <td key={c.id} className={c.type === "num" ? "repvi-num" : ""}>
                            {c.id === "dlstatus" ? <StatusBadge status={row[c.id]} /> : fmtVal(c, row[c.id])}
                          </td>
                        ))}
                      </tr>
                    ))}
              </tbody>
              <tfoot>
                <tr className="repvi-grand">
                  {report.cols.map((c, i) => (
                    <td key={c.id} className={c.type === "num" ? "repvi-num" : ""}>
                      {i === 0 ? "Grand Total" : (isAmountCol(c) ? fmtTotal(c, grandTotals[c.id]) : "")}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* EMAIL MODAL */}
      {emailOpen && (
        <div className="repvi-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setEmailOpen(false); }}>
          <div className="repvi-modal">
            <div className="repvi-modal-head">
              <div className="repvi-modal-icon">✉</div>
              <div>
                <div className="repvi-modal-title">Email Report</div>
                <div className="repvi-modal-sub">{report ? report.name : "—"}</div>
              </div>
              <button className="repvi-modal-x" onClick={() => setEmailOpen(false)}>✕</button>
            </div>
            <div className="repvi-modal-body">
              <div className="repvi-field" style={{ marginBottom: 13 }}>
                <label>To <span className="repvi-req">*</span></label>
                <input type="text" value={emTo} onChange={(e) => setEmTo(e.target.value)} placeholder="name@company.com, ..." />
              </div>
              <div className="repvi-field" style={{ marginBottom: 13 }}>
                <label>CC</label>
                <input type="text" value={emCc} onChange={(e) => setEmCc(e.target.value)} placeholder="optional" />
              </div>
              <div className="repvi-field" style={{ marginBottom: 13 }}>
                <label>Subject</label>
                <input type="text" value={emSubject} onChange={(e) => setEmSubject(e.target.value)} />
              </div>
              <div className="repvi-field" style={{ marginBottom: 13 }}>
                <label>Message</label>
                <textarea rows={4} value={emBody} onChange={(e) => setEmBody(e.target.value)} />
              </div>
              <div className="repvi-field">
                <label>Attachment</label>
                <div className="repvi-attach-opts">
                  <div className={`repvi-attach-opt${attachFmt === "excel" ? " repvi-sel" : ""}`} onClick={() => setAttachFmt("excel")}>
                    <span className="repvi-attach-ico">📊</span><span className="repvi-attach-name">Excel</span>
                  </div>
                  <div className={`repvi-attach-opt${attachFmt === "pdf" ? " repvi-sel" : ""}`} onClick={() => setAttachFmt("pdf")}>
                    <span className="repvi-attach-ico">📄</span><span className="repvi-attach-name">PDF</span>
                  </div>
                  <div className={`repvi-attach-opt${attachFmt === "both" ? " repvi-sel" : ""}`} onClick={() => setAttachFmt("both")}>
                    <span className="repvi-attach-ico">🗂️</span><span className="repvi-attach-name">Both</span>
                  </div>
                </div>
                <div className="repvi-attach-note">The report will be attached and sent via the system mailer. You can also download the file and attach it from your own mail client.</div>
              </div>
            </div>
            <div className="repvi-modal-foot">
              <button className="repvi-btn repvi-btn-outline" onClick={() => setEmailOpen(false)}>Cancel</button>
              <button className="repvi-btn repvi-btn-outline" onClick={openMailClient}>📤 Open in Mail App</button>
              <button className="repvi-btn repvi-btn-success" onClick={sendEmail}>➤ Send</button>
            </div>
          </div>
        </div>
      )}

      {/* PRINT AREA (hidden, filled just before window.print) */}
      <div className="repvi-print-area" ref={printAreaRef}></div>

      {/* TOASTS */}
      <div className="repvi-toast-wrap">
        {toasts.map((t) => {
          const icons = { ok: "✓", err: "✕", warn: "⚠", info: "ℹ" };
          const cls = { ok: "repvi-toast-ok", err: "repvi-toast-err", warn: "repvi-toast-warn", info: "repvi-toast-info" };
          return (
            <div key={t.id} className={`repvi-toast ${cls[t.type] || "repvi-toast-info"}`}>
              <span style={{ fontSize: 15 }}>{icons[t.type] || "ℹ"}</span>{t.msg}
            </div>
          );
        })}
      </div>
    </div>
  );
}
